import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface Customer {
  name: string
  service: string
  date: string
  car?: string
  employeeName?: string // Track which employee's URL was used
  employeeId?: string
  salesPersonName?: string // Track which sales person's URL was used
}

export interface FeedbackData {
  text: string
  rating?: number
  sentiment: 'positive' | 'negative' | 'neutral' | 'inquiry' | null
  category?: 'POSITIVE' | 'NEGATIVE' | 'INQUIRY'
  aiResponse?: string
  keywords?: string[]
  confidence?: number
  reasoning?: string
}

export interface ComplaintData {
  details: string
  priority: 'urgent' | 'normal' | 'low'
  manager: 'brynn' | 'dustin'
  caseNumber?: string
  contact?: {
    phone?: string
    email?: string
    preferredTime?: string
  }
}

export const useFeedbackStore = defineStore('feedback', () => {
  // State
  const customer = ref<Customer>({
    name: '',
    service: '',
    date: '',
    car: '',
    employeeName: '',
    employeeId: '',
    salesPersonName: ''
  })
  
  const feedback = ref<FeedbackData>({
    text: '',
    sentiment: null
  })
  
  const complaint = ref<ComplaintData>({
    details: '',
    priority: 'normal',
    manager: 'brynn'
  })
  
  const caseNumber = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const customerName = computed(() => customer.value.name)
  const hasCustomerInfo = computed(() => !!customer.value.name)
  const isPositiveFeedback = computed(() => feedback.value.sentiment === 'positive')
  const isNegativeFeedback = computed(() => feedback.value.sentiment === 'negative')
  const isInquiry = computed(() => feedback.value.sentiment === 'inquiry')

  // Actions
  function setCustomer(customerData: Customer) {
    customer.value = customerData
  }

  function setFeedback(feedbackData: Partial<FeedbackData>) {
    feedback.value = { ...feedback.value, ...feedbackData }
  }

  function setComplaint(complaintData: Partial<ComplaintData>) {
    complaint.value = { ...complaint.value, ...complaintData }
  }

  function setLoading(loading: boolean) {
    isLoading.value = loading
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage
  }

  function setCaseNumber(caseNum: string | null) {
    caseNumber.value = caseNum
  }

  function reset() {
    customer.value = { name: '', service: '', date: '', car: '', employeeName: '', employeeId: '', salesPersonName: '' }
    feedback.value = { text: '', sentiment: null }
    complaint.value = { details: '', priority: 'normal', manager: 'brynn' }
    caseNumber.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    customer,
    feedback,
    complaint,
    caseNumber,
    isLoading,
    error,
    
    // Getters
    customerName,
    hasCustomerInfo,
    isPositiveFeedback,
    isNegativeFeedback,
    isInquiry,
    
    // Actions
    setCustomer,
    setFeedback,
    setComplaint,
    setLoading,
    setError,
    setCaseNumber,
    reset
  }
}) 